import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Intestazione from "../../assets/intestazione-Giuvi-3.png";
import format from "date-fns/format";
import { it } from "date-fns/locale";

const borderColor = [161, 16, 0];

function toDateObject(timestamp) {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp.toDate === "function")
    return timestamp.toDate();
  if (typeof timestamp === "number") return new Date(timestamp);
  return null;
}

function parseTimeString(timeStr, baseDate) {
  if (!timeStr || typeof timeStr !== "string") return null;
  const m = timeStr.match(/^([0-1]?\d|2[0-3]):([0-5]\d)$/);
  if (!m) return null;
  const d = baseDate instanceof Date ? new Date(baseDate) : new Date();
  d.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
  return d;
}

function roundEntryTimeToNextHalfHour(date) {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  if (minutes === 0) return rounded;
  if (minutes <= 30) rounded.setMinutes(30, 0, 0);
  else rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  return rounded;
}

function roundExitTimeToPreviousHalfHour(date) {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  if (minutes === 0 || minutes === 30) return rounded;
  if (minutes < 30) rounded.setMinutes(0, 0, 0);
  else rounded.setMinutes(30, 0, 0);
  return rounded;
}

async function loadImageAsDataUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const maxWidth = 800; // Larghezza sufficiente per un'intestazione A4
      let width = img.width;
      let height = img.height;

      // Ridimensiona se troppo grande
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      // Sfondo bianco (gestisce trasparenza PNG -> JPEG)
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);

      ctx.drawImage(img, 0, 0, width, height);

      // Converti in JPEG compresso (qualità 0.7)
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function sanitizeFilenamePart(str) {
  return String(str || "")
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .substring(0, 50);
}

export async function downloadPresenzeDipendentePdf({
  presenze,
  user,
  periodo,
  fileName,
  reperibilita,
  isAdmin = false,
}) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  doc.setFont("helvetica", "normal");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginLeft = 15;
  const marginRight = 15;
  const marginTop = 20;

  const headerImgData = await loadImageAsDataUrl(Intestazione);

  const userDisplay =
    user?.anagrafica?.nome && user?.anagrafica?.cognome
      ? `${user.anagrafica.nome} ${user.anagrafica.cognome}`
      : user?.displayName || user?.email || "N/A";

  const periodLabel =
    periodo && typeof periodo === "object"
      ? `${
          [
            "Gennaio",
            "Febbraio",
            "Marzo",
            "Aprile",
            "Maggio",
            "Giugno",
            "Luglio",
            "Agosto",
            "Settembre",
            "Ottobre",
            "Novembre",
            "Dicembre",
          ][periodo.mese]
        } ${periodo.anno}`
      : String(periodo || "N/A");

  // Header & footer per pagina
  const addHeader = (data) => {
    const logoX = marginLeft;
    const logoY = 10;
    const logoW = 50; // larghezza fissa per evitare sovrapposizioni
    const textX = headerImgData ? logoX + logoW + 8 : marginLeft;
    const line1Y = 16;
    const line2Y = 22;

    if (headerImgData) {
      try {
        // altezza calcolata automaticamente mantenendo il rapporto, con larghezza fissa
        // Usa JPEG perché loadImageAsDataUrl ora restituisce JPEG compresso
        doc.addImage(headerImgData, "JPEG", logoX, logoY, logoW, 0);
      } catch (_) {
        // ignora errori immagine senza interrompere la generazione
      }
    }
    doc.setFontSize(12);
    doc.text(`Foglio Presenze - Dipendente: ${userDisplay}`, textX, line1Y);
    doc.text(`Periodo: ${periodLabel}`, textX, line2Y);
  };

  const addFooter = (data) => {
    const pageCount = doc.getNumberOfPages();
    const str = `Pagina ${data.pageNumber} di ${pageCount}`;
    doc.setFontSize(10);
    doc.text(str, pageWidth / 2, pageHeight - 10, { align: "center" });
  };

  // Righe tabella
  const rows = presenze.map((item) => {
    const dataRegistrazioneDate = toDateObject(item.dataRegistrazione);
    let oraIngressoDate = toDateObject(item.oraIngresso);
    let oraUscitaDate = toDateObject(item.oraUscita);
    // Supporta orari in formato stringa HH:mm usando la data del giorno
    if (!oraIngressoDate && typeof item.oraIngresso === "string") {
      oraIngressoDate = parseTimeString(
        item.oraIngresso,
        dataRegistrazioneDate,
      );
    }
    if (!oraUscitaDate && typeof item.oraUscita === "string") {
      oraUscitaDate = parseTimeString(item.oraUscita, dataRegistrazioneDate);
    }

    const giorno = dataRegistrazioneDate
      ? format(dataRegistrazioneDate, "dd - eeee", { locale: it })
      : "-";

    const oraInizio = oraIngressoDate ? format(oraIngressoDate, "HH:mm") : "-";
    const oraFine = oraUscitaDate ? format(oraUscitaDate, "HH:mm") : "-";

    let note = item.note || "-";
    if (item.isFerie) note = "Ferie";
    else if (item.isMalattia)
      note = `Malattia ${
        item.codiceMalattia ? `(${item.codiceMalattia})` : ""
      }`;

    let cantiere = "-";
    const cRaw = item.cantiere ?? item.cantiereSelezionato ?? null;
    if (typeof cRaw === "string") {
      cantiere = cRaw;
    } else if (cRaw && typeof cRaw === "object") {
      cantiere = cRaw.nome || cRaw.label || cRaw.id || "-";
    }

    // Calcolo Ore
    let pausa = "-";
    let totaleOre = "-";

    if (oraIngressoDate && oraUscitaDate && !item.isFerie && !item.isMalattia) {
      const ingressoRounded = roundEntryTimeToNextHalfHour(oraIngressoDate);
      const uscitaRounded = roundExitTimeToPreviousHalfHour(oraUscitaDate);
      const diffMs = uscitaRounded - ingressoRounded;
      let diffHours = diffMs / (1000 * 60 * 60);

      // Gestione Pausa
      let pausaHours = 0;
      if (item.durataPausa && item.durataPausa !== "nessuno") {
        pausaHours = parseFloat(item.durataPausa);
        if (!Number.isNaN(pausaHours)) {
          diffHours -= pausaHours;
          pausa = `${pausaHours}h`;
        }
      }
      diffHours = Math.max(0, diffHours);

      totaleOre = diffHours > 0 ? `${diffHours.toFixed(2)}h` : "-";
    }

    // Colonne richieste:
    // giorno, ora ingresso, ora uscita, pausa, totale ore, nome cantiere, note
    return [giorno, oraInizio, oraFine, pausa, totaleOre, cantiere, note];
  });

  const totals = presenze.reduce(
    (acc, item) => {
      const inDate = toDateObject(item.oraIngresso);
      const outDate = toDateObject(item.oraUscita);
      const baseDate = toDateObject(item.dataRegistrazione);
      const inDateResolved =
        inDate ||
        (typeof item.oraIngresso === "string"
          ? parseTimeString(item.oraIngresso, baseDate)
          : null);
      const outDateResolved =
        outDate ||
        (typeof item.oraUscita === "string"
          ? parseTimeString(item.oraUscita, baseDate)
          : null);

      if (
        inDateResolved &&
        outDateResolved &&
        !item.isFerie &&
        !item.isMalattia
      ) {
        const ingressoRounded = roundEntryTimeToNextHalfHour(inDateResolved);
        const uscitaRounded = roundExitTimeToPreviousHalfHour(outDateResolved);
        const diffMs = uscitaRounded - ingressoRounded;
        let diffHours = diffMs / (1000 * 60 * 60);

        if (item.durataPausa && item.durataPausa !== "nessuno") {
          const p = parseFloat(item.durataPausa);
          if (!Number.isNaN(p)) diffHours -= p;
        }
        diffHours = Math.max(0, diffHours);

        if (diffHours > 0) {
          acc.workingDays += 1;
          acc.totalHours += diffHours;
          const d = baseDate;
          const isWeekend = d && (d.getDay() === 0 || d.getDay() === 6);
          if (isWeekend) {
            acc.straordinario += diffHours;
            acc.weekendStraord += diffHours;
          } else {
            if (diffHours <= 8) {
              acc.ordinarie += diffHours;
            } else if (diffHours <= 9) {
              acc.ordinarie += 8;
              acc.viaggio += diffHours - 8;
            } else {
              acc.ordinarie += 8;
              acc.viaggio += 1;
              acc.straordinario += diffHours - 9;
            }
          }
        }
      }
      return acc;
    },
    {
      totalHours: 0,
      workingDays: 0,
      ordinarie: 0,
      viaggio: 0,
      straordinario: 0,
      weekendStraord: 0,
    },
  );

  // Tabella
  autoTable(doc, {
    startY: marginTop + 18,
    head: [["Data", "In.", "Out", "Pausa", "Totale", "Cantiere", "Note"]],
    body: rows,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 1, halign: "center", valign: "middle" },
    headStyles: { fillColor: borderColor, textColor: 255, halign: "center" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: {
      0: { cellWidth: 28, halign: "left" }, // Data
      5: { cellWidth: "auto", halign: "left" }, // Cantiere
      6: { cellWidth: 40, halign: "left" }, // Note
    },
    margin: { top: marginTop + 18, left: marginLeft, right: marginRight },
    didDrawPage: (data) => {
      addHeader(data);
      addFooter(data);
    },
  });

  // Reperibilità Table
  if (reperibilita && reperibilita.length > 0) {
    const repRows = reperibilita.map((item) => {
      const start = toDateObject(item.dataInizio);
      const end = toDateObject(item.dataFine);
      const startStr = start ? format(start, "dd/MM/yyyy") : "-";
      const endStr = end ? format(end, "dd/MM/yyyy") : "-";

      let duration = "-";
      if (start && end) {
        const diffTime = end - start;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        duration = `${diffDays} giorni`;
      }

      return [startStr, endStr, duration, item.note || "-"];
    });

    let finalY = doc.lastAutoTable.finalY || marginTop + 18;

    // Check for page break if close to bottom
    if (finalY > pageHeight - 50) {
      doc.addPage();
      finalY = marginTop;
    }

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Reperibilità", marginLeft, finalY + 10);

    autoTable(doc, {
      startY: finalY + 15,
      head: [["Data Inizio", "Data Fine", "Durata", "Note"]],
      body: repRows,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 2, halign: "left" },
      headStyles: { fillColor: borderColor, textColor: 255, halign: "center" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: marginTop + 18, left: marginLeft, right: marginRight },
      didDrawPage: (data) => {
        addHeader(data);
        addFooter(data);
      },
    });
  }

  // Aggiungi totali nell'ultima pagina
  const pageCount = doc.getNumberOfPages();
  doc.setPage(pageCount);
  doc.setFontSize(10);
  const summaryY = pageHeight - 20;

  let xPos = marginLeft;
  doc.text(`Giorni: ${totals.workingDays}`, xPos, summaryY);
  xPos += 30;
  doc.text(`Ord: ${totals.ordinarie.toFixed(2)}h`, xPos, summaryY);
  xPos += 30;
  doc.text(`Viag: ${totals.viaggio.toFixed(2)}h`, xPos, summaryY);
  xPos += 30;
  doc.text(`Str: ${totals.straordinario.toFixed(2)}h`, xPos, summaryY);

  const totalStr = `TOT: ${totals.totalHours.toFixed(2)}h`;
  doc.text(totalStr, pageWidth - marginRight, summaryY, { align: "right" });

  const anagrafica = user?.anagrafica || {};
  const valPasto = parseFloat(anagrafica.pasto) || 0;
  const valViaggio = parseFloat(anagrafica.oreViaggio) || 0;
  const valStraord = parseFloat(anagrafica.oreStraordinarie) || 0;
  const tipoStipendio = anagrafica.tipoStipendio || "Contratto";

  const importoPasti = totals.workingDays * valPasto;
  const importoViaggio = totals.viaggio * valViaggio;
  const importoStraord = totals.straordinario * valStraord;
  const importoStraordWE = (totals.weekendStraord || 0) * valStraord;

  const formatEuro = (val) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(val);

  if (isAdmin) {
    const moneyY = summaryY + 6;
    xPos = marginLeft;
    doc.setFont("helvetica", "bold");

    if (tipoStipendio === "Fisso") {
      const importoFisso = parseFloat(anagrafica.importoFisso) || 0;
      doc.text(`Pasti: -`, xPos, moneyY);
      doc.text(`Ord: ${formatEuro(importoFisso)}`, xPos + 30, moneyY);
      doc.text(`Viaggio: -`, xPos + 60, moneyY);
      doc.text(`Straord: -`, xPos + 90, moneyY);
      doc.text(
        `TOT Rimborsi: ${formatEuro(importoFisso)}`,
        pageWidth - marginRight,
        moneyY,
        { align: "right" },
      );
    } else if (tipoStipendio === "Fisso+Sabato") {
      const importoFisso = parseFloat(anagrafica.importoFisso) || 0;
      const totaleImporti = importoStraordWE + importoFisso;
      doc.text(`Pasti: -`, xPos, moneyY);
      doc.text(`Ord: ${formatEuro(importoFisso)}`, xPos + 30, moneyY);
      doc.text(`Viaggio: -`, xPos + 60, moneyY);
      doc.text(`Straord: ${formatEuro(importoStraordWE)}`, xPos + 90, moneyY);
      doc.text(
        `TOT Rimborsi: ${formatEuro(totaleImporti)}`,
        pageWidth - marginRight,
        moneyY,
        { align: "right" },
      );
    } else if (tipoStipendio === "Fisso+Straordinari") {
      const importoFisso = parseFloat(anagrafica.importoFisso) || 0;
      const totaleImporti =
        importoPasti + importoViaggio + importoStraord + importoFisso;
      doc.text(`Pasti: ${formatEuro(importoPasti)}`, xPos, moneyY);
      doc.text(`Ord: ${formatEuro(importoFisso)}`, xPos + 30, moneyY);
      doc.text(`Viaggio: ${formatEuro(importoViaggio)}`, xPos + 60, moneyY);
      doc.text(`Straord: ${formatEuro(importoStraord)}`, xPos + 90, moneyY);
      doc.text(
        `TOT Rimborsi: ${formatEuro(totaleImporti)}`,
        pageWidth - marginRight,
        moneyY,
        { align: "right" },
      );
    } else {
      const totaleImporti = importoPasti + importoViaggio + importoStraord;
      doc.text(`Pasti: ${formatEuro(importoPasti)}`, xPos, moneyY);
      doc.text(`Ord: Contratto`, xPos + 30, moneyY);
      doc.text(`Viaggio: ${formatEuro(importoViaggio)}`, xPos + 60, moneyY);
      doc.text(`Straord: ${formatEuro(importoStraord)}`, xPos + 90, moneyY);
      doc.text(
        `TOT Rimborsi: ${formatEuro(totaleImporti)}`,
        pageWidth - marginRight,
        moneyY,
        { align: "right" },
      );
    }
    doc.setFont("helvetica", "normal");
  }

  // Nome file
  let finalName = fileName;
  if (!finalName) {
    const userName = sanitizeFilenamePart(
      user?.displayName || user?.email || "utente",
    );
    const dateStr =
      periodo && typeof periodo === "object"
        ? format(new Date(periodo.anno, periodo.mese, 1), "MM-yyyy")
        : "";
    finalName = `presenze_${userName}_${dateStr}.pdf`;
  }

  doc.save(finalName);
}
